import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Link } from 'react-router-dom';
import { useUtilisationSummary } from '@/api/hooks/useUtilisation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { UtilisationBucket } from '@/types/utilisation';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

const BUCKET_COLOUR: Record<UtilisationBucket, string> = {
  active: 'hsl(142 71% 45%)',
  intermittent: 'hsl(48 96% 53%)',
  idle: 'hsl(24 95% 53%)',
  ghost: 'hsl(0 84% 60%)',
};

const BUCKET_LABEL: Record<UtilisationBucket, string> = {
  active: 'Active (<30d)',
  intermittent: 'Intermittent (30–60d)',
  idle: 'Idle (60–180d)',
  ghost: 'Ghost (180+d / never)',
};

const BUCKET_DESCRIPTION: Record<UtilisationBucket, string> = {
  active: 'Fuelled in last 30 days',
  intermittent: 'Last fuelled 30–60 days ago',
  idle: 'Last fuelled 60–180 days ago',
  ghost: '180+ days silent or never transacted',
};

const BUCKET_ORDER: UtilisationBucket[] = ['active', 'intermittent', 'idle', 'ghost'];

export default function Utilisation() {
  const { data, isLoading, error } = useUtilisationSummary();

  const countsByBucket = new Map<UtilisationBucket, number>(
    (data?.buckets ?? []).map((b) => [b.bucket, b.count]),
  );
  const pieData = BUCKET_ORDER.map((b) => ({
    bucket: b,
    label: BUCKET_LABEL[b],
    count: countsByBucket.get(b) ?? 0,
  }));

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Utilisation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Active vs idle vs ghost vehicles, inferred from fuel-transaction recency.
        </p>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load utilisation data: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {BUCKET_ORDER.map((b) => (
          <Card
            key={b}
            style={{ borderLeft: `4px solid ${BUCKET_COLOUR[b]}` }}
          >
            <CardHeader className="pb-1.5 pt-3">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                {BUCKET_LABEL[b]}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-3 pt-0">
              {isLoading || !data ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <div className="text-2xl font-semibold tabular-nums">
                  {NUM.format(countsByBucket.get(b) ?? 0)}
                </div>
              )}
              <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                {BUCKET_DESCRIPTION[b]}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid flex-1 grid-cols-1 gap-3 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Tier breakdown</CardTitle>
          </CardHeader>
          <CardContent className="h-72 px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="count"
                    nameKey="label"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={1}
                  >
                    {pieData.map((row) => (
                      <Cell key={row.bucket} fill={BUCKET_COLOUR[row.bucket]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      NUM.format(value),
                      name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Top 50 ghost vehicles (active assets, 180+ days silent)
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto px-0">
            {isLoading ? (
              <div className="space-y-2 px-6 pb-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-7 w-full" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Registration</TableHead>
                    <TableHead>Make / Model</TableHead>
                    <TableHead>Cost centre</TableHead>
                    <TableHead>Last txn</TableHead>
                    <TableHead className="text-right">Days silent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.ghost_vehicles.map((v) => (
                    <TableRow key={v.asset_token}>
                      <TableCell className="font-mono text-xs">
                        <Link
                          to={`/vehicles/${v.asset_token}`}
                          className="text-foreground hover:underline"
                        >
                          {v.registration ?? '—'}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {[v.make, v.model].filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.cost_centre_code ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.last_txn_time
                          ? new Date(v.last_txn_time).toLocaleDateString('en-ZA', {
                              dateStyle: 'medium',
                            })
                          : 'never'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {v.days_silent != null ? NUM.format(v.days_silent) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data && data.ghost_vehicles.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-sm text-muted-foreground"
                      >
                        No ghost vehicles — every active asset has fuelled in the last 180 days.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-muted bg-muted/30">
        <CardContent className="py-3 text-xs text-muted-foreground">
          Utilisation is inferred from fuel-transaction recency because the
          movement-event feed (<code>fleet.vehicle_movement</code>) is not yet
          populated. Buckets: <strong>active</strong> &lt; 30 d,{' '}
          <strong>intermittent</strong> 30–60 d, <strong>idle</strong> 60–180 d,{' '}
          <strong>ghost</strong> ≥ 180 d (or no txn ever). Anchor{' '}
          <code>data_now</code> ={' '}
          {data
            ? new Date(data.data_now).toLocaleString('en-ZA', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : '…'}
          .
        </CardContent>
      </Card>
    </div>
  );
}
